import { SortationCenterError, SortationGaylordError } from "@deliverr/sortation-client";
import { createSuccessNotification, log, logError, logStart } from "@deliverr/ui-facility";
import { useCommonFlow } from "@deliverr/ui-facility/lib-facility/flow/useCommonFlow";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useAsyncFn } from "react-use";
import { useRecoilValue } from "recoil";

import { sortationIdState } from "sortation/base/sortationIdState";
import { useSortationCenterError } from "sortation/base/useSortationCenterError";
import { useSortationClient } from "sortation/base/useSortationClient";
import { useSortationModal } from "sortation/components/SortationModal";
import { visibleSortationModalValue } from "sortation/components/SortationModal/SortationModalState";
import { TOAST_AUTO_CLOSE } from "sortation/utils/toastAutoClose";
import { TransferScanModal, transferScanMessageMap } from "./modals";

export const useTransferScan = () => {
  const { formatMessage } = useIntl();
  const { successResponse, infoResponse, resetNotifications, addAutoCloseNotification, emitFlash } = useCommonFlow();
  const { handleSortationCenterError } = useSortationCenterError();
  const sortationId = useRecoilValue(sortationIdState);
  const sortationClient = useSortationClient();
  const { showModal } = useSortationModal();
  const visibleModal = useRecoilValue(visibleSortationModalValue);

  const [barcode, setBarcode] = useState<string>("");

  const handleError = useCallback(
    (err) => {
      const errCode = err.message;
      let modal: TransferScanModal;

      switch (errCode) {
        case SortationGaylordError.NOT_FOUND:
          modal = TransferScanModal.GAYLORD_NOT_FOUND;
          break;
        case SortationGaylordError.GAYLORD_NOT_ELIGIBLE_FOR_TRANSFER:
          modal = TransferScanModal.GAYLORD_NOT_ELIGIBLE;
          break;
        default:
          modal = TransferScanModal.UNKNOWN_ERROR;
          break;
      }

      emitFlash("DANGER");
      showModal(modal, transferScanMessageMap[modal]);
    },
    [emitFlash, showModal]
  );

  const [handleTransferScanState, handleTransferScan] = useAsyncFn(
    async (input: string) => {
      if (visibleModal) {
        return;
      }

      const ctx = logStart({ fn: "useTransferScan.handleTransferScan", input, sortationId });

      setBarcode(input);
      resetNotifications();

      try {
        const response = await sortationClient.receiveGranularZoneSkipGaylord(input, sortationId!);

        if (response.isTransferAlreadyCompleted) {
          infoResponse();
          addAutoCloseNotification(
            createSuccessNotification(
              formatMessage({
                id: "sortation.transferScan.alreadyCompleted",
                defaultMessage: "Transfer already completed for this gaylord",
              })
            ),
            TOAST_AUTO_CLOSE
          );
          log(ctx, "Transfer already completed", { eventId: response.eventId });
          return;
        }

        successResponse();
        addAutoCloseNotification(
          createSuccessNotification(
            formatMessage(
              {
                id: "sortation.transferScan.success",
                defaultMessage: "Processing gaylord transfer: {origin} to {destination}",
              },
              {
                origin: response.originGaylordId,
                destination: response.destinationGaylordId,
              }
            )
          ),
          TOAST_AUTO_CLOSE
        );

        log(ctx, "Transfer initiated successfully", { eventId: response.eventId });
      } catch (err) {
        logError(ctx, err);
        if (err.message === SortationCenterError.SORTATION_CENTER_NOT_FOUND) {
          handleSortationCenterError();
          return;
        }
        handleError(err);
      } finally {
        setBarcode("");
      }
    },
    [
      sortationId,
      visibleModal,
      formatMessage,
      resetNotifications,
      sortationClient,
      infoResponse,
      successResponse,
      addAutoCloseNotification,
      handleSortationCenterError,
      handleError,
    ]
  );

  return {
    barcode,
    isLoading: handleTransferScanState.loading,
    setBarcode,
    handleTransferScan,
  };
};
